import {
  formatLocDateMMDDYYYY,
  formatPostDate,
} from '@mummssoftware/utils/formatters';
import { HIS, HC } from '@mummssoftware/utils/roleData';
import { APR, ACCPT, DWN } from './constants';
import { typeMap } from '../HospiceItemSet/constants';
import saveAs from '../FileSaver/FileSaver';
import {
  ApiError,
  approvedBy,
  acceptedBy,
  inDays,
  daysAgo,
} from '../../translations/en.json';

// modifies the row data depending on action
export const updateGridRow = (
  response: any,
  row: any,
  formattMessage: any,
  type: any,
) => {
  const {
    hisadminstatusshortname,
    personfirstname,
    personlastname,
    personcredentials,
    statusdatetimeshifted,
  } = response;
  row.possibleactions = `[${type}]`;
  const formattedDate = formatLocDateMMDDYYYY(statusdatetimeshifted);
  const messageData = {
    personfirstname,
    personlastname,
    personcredentials,
    formattedDate,
  };

  switch (hisadminstatusshortname) {
    case APR:
      row.approvaleventdisplay = formattMessage(approvedBy, messageData);
      break;
    case ACCPT:
      row.accepteventdisplay = formattMessage(acceptedBy, messageData);
      break;
    case DWN:
      row.downloadeventdisplay = '';
      row.lastdownloadadmineventid = null;
      break;
    default:
  }
  return null;
};

// filters out rows 'ready for download'
export const getReadyForDownload = (hisRows: any) =>
  hisRows.reduce((acc: any, row: any) => {
    if (row.possibleactions?.includes(DWN) && !row.lastdownloadadmineventid)
      acc.push(row);
    return acc;
  }, []);

// determines if row is past or future and formats statement accordingly
// also provides color to display date, secondary if it is less than 15 days till due
export const formatDueStatement = (
  date: string,
  formatMessage: any,
  palette: any,
) => {
  const futureDate = new Date(date) > new Date();
  const numOfDays = Math.ceil(
    Math.abs((new Date() as any) - (new Date(date) as any)) /
      (1000 * 60 * 60 * 24),
  );

  const statementColor =
    futureDate && numOfDays > 15
      ? palette.getContrastText(palette.background.paper)
      : palette.text.secondary;

  return [
    statementColor,
    futureDate
      ? formatMessage(inDays, { numOfDays })
      : formatMessage(daysAgo, { numOfDays }),
  ];
};

// take xml DTOs and loop over them to add them to zip folder by patient number.
export const zipHISFiles = (
  xmlResponse: any,
  HISZipFolder: any,
  getCrossPatientHIS: any,
  downloadedRows: any,
) => {
  const hashedRows = downloadedRows.reduce((seed: any, row: any) => {
    seed[`${row.patientnumber}-${row.type}-${row.episodenum}`] = row;
    return seed;
  }, {});
  xmlResponse.forEach((xml: any) => {
    const identifier = `${xml.patientnumber}-${xml.type}-${xml.episodenum}`;
    if (hashedRows[identifier]) {
      const { type, episodenum } = hashedRows[identifier];
      HISZipFolder.file(
        `${xml.patientnumber}_hospice_data_${
          typeMap.get(type)[1]
        }_${episodenum}.xml`,
        xml.unsentBatchHISXML,
      );
    }
  });
  HISZipFolder.generateAsync({ type: 'blob' })
    .then((content: any) => {
      saveAs(content, 'hospice_data.zip');
    })
    .then(() => {
      getCrossPatientHIS();
    });
};

// formats api errors to the appropriate error message
export const formatApiError = (
  response: any,
  formatMessage: any,
  service: string,
) => {
  const { status } = response;
  return {
    text: formatMessage(ApiError, { status, service }),
    severity: 'error',
  };
};

// format dto for posting
export const getPostData = (
  data: any,
  personnumber: any,
  hisadminstatusshortname: string,
) => {
  const { patientnumber, siteid, type, episodenum, admitdate } = data;
  return {
    personnumber,
    episodenum,
    patientnumber,
    admitdate,
    siteid,
    statusdatetime: formatPostDate(Date.now()),
    hisadminstatusshortname,
    type,
  };
};

export const formatModalTitle = (data: any) => {
  const { patientnumber, patientfirstname, patientlastname, type } = data;
  return `${patientnumber} - ${patientfirstname} ${patientlastname} - ${
    typeMap.get(type)[0]
  }`;
};

export const findApprovalRoles = (currentUser: mumms.User) =>
  !!(
    currentUser?.userRoles?.find((rg: string) => rg === HC) ||
    currentUser?.userRoleGroups?.find((rg: string) => rg === HIS) ||
    currentUser?.isHospiceAdmin ||
    currentUser.isMumms
  );
